/**
 * Component: SummaryCardsGroup
 * Renders top metric summary cards (approximate figma layout)
 */

import type { DashboardSummary } from "../../../../domain/model/DashboardModel";
import {
  ArrowBottomRightIcon,
  UserYellowIcon,
  VoucherYellowIcon,
  TransactionYellowIcon,
  MerchantYellowIcon,
  ArrowTopRightIcon,
  CircleBackgroundGreyIcon,
} from "@/assets/svg";

interface SummaryCardsGroupProps {
	  readonly summary: DashboardSummary;
	}

type SummaryItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  value: string | number;
  delta?: string;
};

const formatGrowthDelta = (value: number): string => {
	  const formatted = value.toFixed(1);
	  return value > 0 ? `+${formatted}%` : `${formatted}%`;
	};

export const SummaryCardsGroup = ({
  summary,
}: SummaryCardsGroupProps): React.JSX.Element => {
  const {
    userRegistered,
    totalPoints,
    registeredMerchant,
    voucherRedeemed,
  } = summary;

  const items: SummaryItem[] = [
    {
      id: "registeredUsers",
      label: "User Registered",
      icon: <UserYellowIcon />,
      value: userRegistered.currentValue.toLocaleString("id-ID"),
      delta: formatGrowthDelta(userRegistered.growthPercentage),
    },
    {
      id: "totalPoints",
      label: "Total Points",
      icon: <TransactionYellowIcon />,
      value: totalPoints.currentValue.toLocaleString("id-ID"),
      delta: formatGrowthDelta(totalPoints.growthPercentage),
    },
    {
      id: "activeUsers",
      label: "Registered Merchant",
      icon: <MerchantYellowIcon />,
      value: registeredMerchant.currentValue.toLocaleString("id-ID"),
      delta: formatGrowthDelta(registeredMerchant.growthPercentage),
    },
    {
      id: "growth",
      label: "Voucher Redeemed",
      icon: <VoucherYellowIcon />,
      value: voucherRedeemed.currentValue.toLocaleString("id-ID"),
      delta: formatGrowthDelta(voucherRedeemed.growthPercentage),
    },
  ];

  return (
    <div className="grid grid-cols-4 gap-4 mb-5 max-xl:grid-cols-2">
      {items.map((item) => (
        <div key={item.id} className="flex flex-col rounded-lg border border-[#d1d3db] p-4 gap-[23px] text-[#001a41] relative overflow-hidden">
          <div className="absolute bottom-[-16px] right-0">
            <CircleBackgroundGreyIcon />
          </div>
          <div className="flex justify-between">
            <div className="text-xl leading-none">{item.icon}</div>
            <div className="font-semibold text-xs leading-4 text-right max-w-[70px]">{item.label}</div>
          </div>
          <div className="flex flex-col gap-1 items-start text-left">
            <div className="font-raleway font-bold text-[30px] leading-9 tracking-[0.005em]">{item.value}</div>
            <div
              className={`font-normal text-xs leading-4 tracking-[0.005em] flex items-center gap-1 ${
                item.delta?.slice(0, 1) === "-" ? "text-[#c2001c]" : "text-[#007c4c]"
              }`}
            >
              {item.delta?.slice(0, 1) === "-" ? (
                <ArrowBottomRightIcon />
              ) : (
                <ArrowTopRightIcon />
              )}
              {item.delta?.slice(1)}
              {" from previous period"}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
